import React, { useContext, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import NavigationPrompt from 'react-router-navigation-prompt'
import styled from 'styled-components'
import { showNotification } from 'features/notifications'
import { useQuery, useSlug, useEventSlug } from 'hooks'
import { getNodes } from 'graphql/utils'
import { Shape } from 'features/map/types'
import { mapSpaceQuery } from 'features/map/graphql/query'
import { LayoutContext, TOP_NAV_HEIGHT } from 'components/layout/Layout'
import { _ } from 'utils'
import Draw from './components/Draw'
import ControlsOverlay from './components/ControlsOverlay'
import { DrawItem } from './types'
import {
    nestCoords
} from './utils'

import {
    MapItemFragment,
    MapSpaceQuery,
    MapSpaceQueryVariables
} from '../../generated/graphql'

export interface DrawImageMapPops {
    image: string
    image_height?: number
    image_width?: number
    ignoreMouseClicks?: boolean
    offsetHeight?: number
}

const S = {
    Wrapper: styled.div<{ width: number, height: number }>`
        width: ${({ width }) => width}px;
        height: ${({ height }) => height}px;
        overflow: hidden;
    `,
    ImageMapWrapper: styled.div<DrawImageMapPops>`
        position: relative;
        width: ${({ image_width }) => image_width}px;
        height: ${({ image_height }) => image_height}px;
        transform: translateY(-${({ offsetHeight }) => offsetHeight}px);
    `,
    ItemsControl: styled.div`
        width: 100%;
        display: flex;
        flex-wrap: wrap;
    `,
    ItemControl: styled.div`
        padding: 12px;
        margin: 12px;
        border-radius: 6px;
    `,
}

const convertMapItem = (item: MapItemFragment, mapId): DrawItem => ({
    mapId,
    generatedId: item.id,
    ...item,
    coords: nestCoords(item.coords),
    shape: item.shape as Shape,
    title: item.name,
})

const DrawImageMap = ({ image_height, image_width, image }: DrawImageMapPops) => {
    const dispatch = useDispatch()
    const { screenWidth, screenHeight, scaledSidebar } = useContext(LayoutContext)
    const imageRatio = image_width / image_height
    const imageWidth = screenWidth - scaledSidebar + 4
    const imageHeight = imageWidth / imageRatio
    const scaledFactor = imageHeight / image_height
    const tooMuchHeight = imageHeight - (screenHeight - TOP_NAV_HEIGHT)
    const eventSlug = useEventSlug()
    const spaceSlug = useSlug('mapSlug')
    const { data, fetchData: callGetMapItems } = useQuery<MapSpaceQuery, MapSpaceQueryVariables>(
        mapSpaceQuery, { eventSlug, filters: { parent: { slug: spaceSlug } } }
    )
    const mapSpaceData = getNodes(data?.event?.mapSpaces)[0]
    const mapItems = useMemo(() => (
        (mapSpaceData?.items || []).map((e) => convertMapItem(e, mapSpaceData?.id))
    ), [mapSpaceData?.id, mapSpaceData?.items])
    const unlinkedItems = mapItems.filter(
        (item) => !item.space && !item.widget && !item.forMobile
    ).map((e) => e.title)

    return (
        <S.Wrapper
            width={imageWidth}
            height={screenHeight - TOP_NAV_HEIGHT + 4}
        >
            <S.ImageMapWrapper
                image={image}
                image_width={imageWidth}
                image_height={imageHeight}
                offsetHeight={tooMuchHeight/2}
            >
                <Draw
                    mapId={mapSpaceData?.id}
                    items={mapItems}
                    callGetMapItems={callGetMapItems}
                    image={image}
                    image_width={imageWidth}
                    image_height={imageHeight}
                />
                <ControlsOverlay
                    items={mapItems}
                    callGetMapItems={callGetMapItems}
                    scaledFactor={scaledFactor}
                />
            </S.ImageMapWrapper>
            <NavigationPrompt when={unlinkedItems.length > 0}>
                {({ onCancel }) => {
                    onCancel()
                    dispatch(showNotification({
                        type: 'warning',
                        body: _('map.unlinked_areas', { areas: unlinkedItems.join(', ') }),
                        displayDuration: 7000,
                        id: 999,
                    }))
                    return <></>
                }}
            </NavigationPrompt>
        </S.Wrapper>
    )
}

export default DrawImageMap
